import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Heart, MessageSquare, Search } from "lucide-react";
import { api, img, errMsg } from "../lib/api";
import { useApp } from "../context/AppContext";
import { ProductGrid } from "../components/ProductCard";
import { CollectionCard } from "../components/HomeSections";
import { PageHead } from "./Shop";
import { ThreadView } from "./Partner";

import { StoreInfo, StoreAnnouncement } from "../components/StoreInfo";
import { tx, txf, useLang } from "../lib/i18n";
export const NotFound = ({ msg = "This page could not be found." }) => { useLang(); return (
  <div className="mv-container py-24 text-center" data-testid="not-found">
    <h1 className="text-3xl font-semibold">{tx(msg)}</h1>
    <Link to="/" className="mt-6 inline-block text-[var(--mv-teal)]">{tx("Back to MIVO")}</Link>
  </div>
); };

export function CollectionsIndex() { const lang = useLang();
  const { t } = useApp();
  const [cols, setCols] = useState([]);
  useEffect(() => { api.get("/collections").then((r) => setCols(r.data)); }, [lang]);
  return (
    <div className="mv-container" data-testid="collections-page">
      <PageHead eyebrow="MIVO" title={t("nav.collections")} />
      <div className="grid gap-5 md:grid-cols-2">{cols.map((c, i) => <CollectionCard key={c.slug} c={c} idx={i} />)}</div>
    </div>
  );
}

export function CollectionPage() { const lang = useLang();
  const { slug } = useParams();
  const { t } = useApp();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { setData(null); api.get(`/collections/${slug}`).then((r) => setData(r.data)).catch(() => setErr(true)); }, [slug, lang]);
  if (err) return <NotFound msg="Collection not found" />;
  if (!data) return <div className="mv-container py-24 text-[var(--mv-muted)]">{t("common.loading")}</div>;
  const c = data.collection;
  return (
    <div data-testid="collection-page">
      <div className="relative h-[300px] overflow-hidden">
        <img src={img(c.image)} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#05090b] via-[#05090b]/70 to-transparent" />
        <div className="mv-container relative flex h-full flex-col justify-end pb-10">
          <p className="text-xs font-semibold tracking-[0.16em] text-[var(--mv-teal)]">{tx(c.eyebrow)}</p>
          <h1 className="mt-3 text-4xl sm:text-5xl font-semibold" data-testid="collection-title">{tx(c.headline)}</h1>
          <p className="mt-2 text-sm text-[var(--mv-muted)]">{tx(data.products.length)} {t("common.results")}</p>
        </div>
      </div>
      <div className="mv-container mt-10">
        {tx(data.products.length ? <ProductGrid items={data.products} /> : <p className="text-[var(--mv-muted)]">{t("common.empty")}</p>)}
      </div>
    </div>
  );
}

const StoreActions = ({ s, onFollowers }) => { useLang();
  const { user } = useApp();
  const nav = useNavigate();
  const [following, setFollowing] = useState(false);
  const [open, setOpen] = useState(false);
  const [m, setM] = useState({ subject: "", body: "" });
  useEffect(() => { if (user) api.get(`/stores/${s.slug}/follow`).then((r) => setFollowing(r.data.following)).catch(() => {}); }, [user, s.slug]);
  const gate = () => { if (!user) { nav(`/login?next=/stores/${s.slug}`); return false; } return true; };
  const toggle = async () => { if (!gate()) return; try { const r = following ? await api.delete(`/stores/${s.slug}/follow`) : await api.post(`/stores/${s.slug}/follow`); setFollowing(r.data.following); onFollowers(r.data.followers); } catch (e) { toast.error(tx(errMsg(e))); } };
  const send = async () => { try { await api.post("/messages", { store_slug: s.slug, ...m }); toast.success(tx("Message sent to the seller")); setOpen(false); setM({ subject: "", body: "" }); } catch (e) { toast.error(tx(errMsg(e))); } };
  return (
    <div className="mb-8 space-y-3">
      <div className="flex gap-2">
        <button onClick={toggle} className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm ${following ? "mv-btn-outline" : "mv-btn-primary"}`} style={following ? { borderColor: "var(--store-accent)", color: "var(--store-accent)" } : { background: "var(--store-accent)", color: "#05090b" }} data-testid="store-follow-button" aria-pressed={following}><Heart className={`h-4 w-4 ${following ? "fill-[var(--mv-teal)] text-[var(--mv-teal)]" : ""}`} />{tx(following ? "Following" : "Follow store")}</button>
        <button onClick={() => gate() && setOpen(!open)} className="mv-btn-outline flex items-center gap-2 rounded-lg px-4 py-2 text-sm" data-testid="store-message-button"><MessageSquare className="h-4 w-4" />{tx("Contact seller")}</button>
      </div>
      {tx(open && <div className="max-w-xl space-y-2 rounded-2xl border border-[var(--mv-border)] bg-[var(--mv-card)] p-4" data-testid="store-message-form">
        <input className="w-full rounded-lg border border-[var(--mv-border-2)] bg-[var(--mv-bg)] px-3 py-2 text-sm" placeholder={tx("Subject")} value={m.subject} onChange={(e) => setM({ ...m, subject: e.target.value })} data-testid="store-message-subject" />
        <textarea rows={3} className="w-full rounded-lg border border-[var(--mv-border-2)] bg-[var(--mv-bg)] px-3 py-2 text-sm" placeholder={tx("Your message")} value={m.body} onChange={(e) => setM({ ...m, body: e.target.value })} data-testid="store-message-body" />
        <button onClick={send} className="mv-btn-primary rounded-lg px-4 py-2 text-sm" data-testid="store-message-send">{tx("Send")}</button>
      </div>)}
    </div>
  );
};

export function StorePage() { const lang = useLang();
  const { slug } = useParams();
  const { t } = useApp();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const nav = useNavigate();
  useEffect(() => { api.get(`/stores/${slug}`).then((r) => { setData(r.data); if (r.data.store.slug !== slug) nav(window.location.pathname.replace(slug, r.data.store.slug), { replace: true }); }).catch(() => setErr(true)); }, [slug, lang, nav]);
  const setFollowers = (n) => setData((d) => ({ ...d, store: { ...d.store, followers: n } }));
  if (err) return <NotFound msg="Store not found" />;
  if (!data) return <div className="mv-container py-24 text-[var(--mv-muted)]">{t("common.loading")}</div>;
  const s = data.store;
  const feat = s.featured_product_ids || [];
  const items = [...data.products].sort((a, b) => (feat.includes(b.product_id) ? 1 : 0) - (feat.includes(a.product_id) ? 1 : 0));
  return (
    <div data-testid="store-page" style={{ "--store-accent": s.accent || "var(--mv-teal)" }}>
      <div className="relative h-[240px] overflow-hidden">
        {tx(s.banner ? <img src={img(s.banner)} alt="" className="absolute inset-0 h-full w-full object-cover opacity-60" /> : <div className="absolute inset-0 opacity-30" style={{ background: "radial-gradient(circle at 70% 30%, var(--store-accent), transparent 60%)" }} />)}
        <div className="absolute inset-0 bg-gradient-to-t from-[#05090b] to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-[3px]" style={{ background: "var(--store-accent)" }} data-testid="store-accent-bar" />
        <div className="mv-container relative flex h-full items-end gap-5 pb-8">
          {tx(s.logo ? <img src={img(s.logo)} alt="" className="h-16 w-16 rounded-2xl border border-[var(--mv-border-2)] object-cover" data-testid="store-logo" /> : <div className="grid h-16 w-16 place-items-center rounded-2xl bg-[var(--mv-card)] border border-[var(--mv-border-2)] text-2xl font-semibold text-[var(--mv-teal)]">{tx(s.name[0])}</div>)}
          <div>
            <h1 className="text-3xl font-semibold" data-testid="store-name">{s.name}</h1>
            {(s.operated_by_mivo || s.is_company_store) && <span className="mt-1 inline-block rounded-full border border-[var(--mv-teal)]/50 px-2 py-0.5 text-[10px] text-[var(--mv-teal)]" data-testid="store-operated-badge">{tx("Operated by MIVO")}</span>}
            {s.slogan && <p className="mt-1 text-sm" style={{ color: "var(--store-accent)" }} data-testid="store-slogan">{tx(s.slogan)}</p>}
            <p className="mt-1 text-sm text-[var(--mv-muted)]">{tx("Grade")} {tx(s.grade)} · {tx(data.products.length)} {tx("products ·")} <span data-testid="store-followers">{txf("{0} followers", s.followers)}</span></p>
          </div>
        </div>
      </div>
      <div className="mv-container mt-8">
        {tx(s.admin_preview && <p className="mb-4 rounded-lg border border-amber-300/40 bg-amber-300/10 px-3 py-2 text-xs text-amber-200" data-testid="store-admin-preview">{tx("PRIVATE PREVIEW — store status “")}{tx(s.status)}”{tx(s.operated_by_mivo ? " · MIVO-operated" : "")}. Only Super Admin can see this page and its draft products until the store is published.</p>)}
        <StoreAnnouncement s={s} />
        <p className="mb-5 max-w-2xl text-sm text-[var(--mv-muted)]" data-testid="store-description">{tx(s.description)}</p>
        <StoreActions s={s} onFollowers={setFollowers} />
        {items.length === 0 ? <div className="mb-10 rounded-2xl border border-dashed border-[var(--mv-border-2)] p-10 text-center" data-testid="store-coming-soon"><p className="text-lg font-semibold">{tx("Products coming soon")}</p><p className="mt-1 text-sm text-[var(--mv-muted)]">{tx("This store is open — follow it to hear when the first products arrive.")}</p></div> : <ProductGrid items={items} />}
        <StoreInfo s={s} />
      </div>
    </div>
  );
}

export function StoresIndex() { const lang = useLang();
  const [stores, setStores] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { const h = setTimeout(() => api.get("/stores", { params: { q: q || undefined } }).then((r) => setStores(r.data)), 250); return () => { clearTimeout(h); }; }, [q, lang]);
  return (
    <div className="mv-container" data-testid="stores-page">
      <PageHead eyebrow={tx("MIVO MARKETPLACE")} title={tx("Stores")} />
      <div className="mb-6 flex max-w-md items-center gap-2 rounded-xl border border-[var(--mv-border-2)] bg-[var(--mv-card)] px-3"><Search className="h-4 w-4 text-[var(--mv-dim)]" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tx("Search stores")} className="flex-1 bg-transparent py-2.5 text-sm outline-none" data-testid="stores-search-input" /></div>
      {tx(stores.length === 0 && <p className="text-sm text-[var(--mv-muted)]" data-testid="stores-empty">{tx("No stores match your search.")}</p>)}
      <div className="grid gap-5 md:grid-cols-2">
        {stores.map((s) => (
          <Link key={s.slug} to={`/stores/${s.slug}`} data-testid={`store-card-${s.slug}`} className="rounded-2xl border border-[var(--mv-border)] bg-[var(--mv-card)] p-6 hover:border-[var(--mv-teal)] transition-colors">
            <div className="flex items-center gap-3">{tx(s.logo && <img src={img(s.logo)} alt="" className="h-10 w-10 rounded-xl object-cover" />)}<p className="text-lg font-semibold">{s.name}</p></div>
            <p className="mt-1 text-xs text-[var(--mv-dim)]">{tx("Grade")} {tx(s.grade)} · {tx(s.count)} products</p>
            <p className="mt-3 text-sm text-[var(--mv-muted)]">{tx(s.description)}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function MessagesPage() { useLang();
  const { user } = useApp();
  const [ts, setTs] = useState([]);
  const [support, setSupport] = useState([]);
  const [m, setM] = useState({ subject: "", body: "" });
  const staff = ["support", "super_admin"].includes(user?.role);
  const load = () => { api.get("/me/messages").then((r) => setTs(r.data)); if (staff) api.get("/support/messages").then((r) => setSupport(r.data)); };
  useEffect(() => { load(); }, [staff]); // eslint-disable-line
  const send = async () => { try { await api.post("/messages", { store_slug: "support", ...m }); setM({ subject: "", body: "" }); toast.success(tx("Sent to MIVO Support")); load(); } catch (e) { toast.error(tx(errMsg(e))); } };
  const reply = (path) => async (body) => { try { await api.post(path, { body }); load(); } catch (e) { toast.error(tx(errMsg(e))); } };
  return (
    <div className="mv-container max-w-3xl" data-testid="messages-page">
      <PageHead eyebrow="MIVO" title={tx("Messages")} />
      {staff && <div className="mb-4 flex gap-5"><Link to="/support/desk" className="text-sm text-[var(--mv-teal)]" data-testid="support-desk-link">{tx("Open Support Center (AI conversations) →")}</Link>
        <Link to="/moderation/reviews" className="text-sm text-[var(--mv-teal)]" data-testid="support-reviews-link">{tx("Open Customer Reviews Center →")}</Link></div>}
      <div className="mb-6 space-y-2 rounded-2xl border border-[var(--mv-border)] bg-[var(--mv-card)] p-4" data-testid="support-message-form">
        <p className="text-sm font-semibold">{tx("Contact MIVO Support")}</p>
        <input className="w-full rounded-lg border border-[var(--mv-border-2)] bg-[var(--mv-bg)] px-3 py-2 text-sm" placeholder={tx("Subject")} value={m.subject} onChange={(e) => setM({ ...m, subject: e.target.value })} data-testid="support-subject" />
        <textarea rows={3} className="w-full rounded-lg border border-[var(--mv-border-2)] bg-[var(--mv-bg)] px-3 py-2 text-sm" placeholder={tx("How can we help?")} value={m.body} onChange={(e) => setM({ ...m, body: e.target.value })} data-testid="support-body" />
        <button onClick={send} className="mv-btn-primary rounded-lg px-4 py-2 text-sm" data-testid="support-send">{tx("Send")}</button>
      </div>
      <div className="space-y-3">{ts.map((t) => <ThreadView key={t.thread_id} t={t} testid={`thread-${t.thread_id}`} onReply={reply(`/me/messages/${t.thread_id}`)} />)}</div>
      {tx(staff && <div className="mt-10 space-y-3" data-testid="support-inbox"><p className="text-sm font-semibold">{tx("Support inbox")}</p>
        {support.map((t) => <ThreadView key={t.thread_id} t={t} testid={`support-thread-${t.thread_id}`} onReply={reply(`/support/messages/${t.thread_id}`)} />)}</div>)}
    </div>
  );
}
